import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { ApiIdentityServiceContext } from "../contexts/ApiIdentityServiceContext";
import type {
  ApiKeyIdentity,
  CreateApiKeyInput,
  EntityIdentity,
} from "../services/apiIdentityService";

// Query keys for API identity-related queries
export const apiIdentityQueryKeys = {
  all: ["apiIdentities"] as const,
  entities: () => [...apiIdentityQueryKeys.all, "entities"] as const,
  apiKeys: () => [...apiIdentityQueryKeys.all, "apiKeys"] as const,
  entity: (id: string) => [...apiIdentityQueryKeys.entities(), id] as const,
  apiKey: (id: string) => [...apiIdentityQueryKeys.apiKeys(), id] as const,
};

/**
 * Hook to get the API identity service instance
 */
function useApiIdentityService() {
  const service = useContext(ApiIdentityServiceContext);
  if (!service) {
    throw new Error(
      "useApiIdentities must be used within a ApiIdentityServiceProvider"
    );
  }
  return service;
}

// ============================================================================
// ENTITY HOOKS
// ============================================================================

/**
 * Hook to fetch all entities
 */
export function useEntityIdentities() {
  const service = useApiIdentityService();

  return useQuery({
    queryKey: apiIdentityQueryKeys.entities(),
    queryFn: () => service.getEntities(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to add a new entity
 */
export function useAddEntityIdentity() {
  const service = useApiIdentityService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entity: EntityIdentity) => service.addEntity(entity),
    onSuccess: () => {
      // Invalidate and refetch entities list
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.entities(),
      });
    },
    onError: (error) => {
      console.error("Failed to add entity:", error);
    },
  });
}

/**
 * Hook to remove an entity
 */
export function useRemoveEntityIdentity() {
  const service = useApiIdentityService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entityId: string) => service.removeEntity(entityId),
    onSuccess: (_, entityId) => {
      // Invalidate entities list
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.entities(),
      });

      // Remove specific entity from cache
      queryClient.removeQueries({
        queryKey: apiIdentityQueryKeys.entity(entityId),
      });
    },
    onError: (error) => {
      console.error("Failed to remove entity:", error);
    },
  });
}

// ============================================================================
// API KEY HOOKS
// ============================================================================

/**
 * Hook to fetch all API keys
 */
export function useApiKeyIdentities() {
  const service = useApiIdentityService();

  return useQuery({
    queryKey: apiIdentityQueryKeys.apiKeys(),
    queryFn: () => service.getApiKeys(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to add a new API key
 */
export function useAddApiKeyIdentity() {
  const service = useApiIdentityService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKeyInput: CreateApiKeyInput & { id: string }) =>
      service.addApiKey(apiKeyInput),
    onSuccess: (_, variables) => {
      // Invalidate and refetch API keys list
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.apiKeys(),
      });

      // Add the new API key to cache optimistically
      queryClient.setQueryData(apiIdentityQueryKeys.apiKey(variables.id), {
        id: variables.id,
        label: variables.label,
        key: variables.key,
        keySuffix:
          variables.key.length >= 5
            ? `...${variables.key.slice(-5)}`
            : `...${variables.key}`,
        userName: variables.userName,
      } as ApiKeyIdentity);
    },
    onError: (error) => {
      console.error("Failed to add API key:", error);
    },
  });
}

/**
 * Hook to remove an API key
 */
export function useRemoveApiKeyIdentity() {
  const service = useApiIdentityService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKeyId: string) => service.removeApiKey(apiKeyId),
    onSuccess: (_, apiKeyId) => {
      // Invalidate API keys list
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.apiKeys(),
      });

      // Remove specific API key from cache
      queryClient.removeQueries({
        queryKey: apiIdentityQueryKeys.apiKey(apiKeyId),
      });
    },
    onError: (error) => {
      console.error("Failed to remove API key:", error);
    },
  });
}

// ============================================================================
// COMBINED HOOKS
// ============================================================================

/**
 * Hook that provides all API identity operations
 * This is a convenience hook that combines all the individual hooks
 */
export function useApiIdentities() {
  const entitiesQuery = useEntityIdentities();
  const apiKeysQuery = useApiKeyIdentities();
  const addEntityMutation = useAddEntityIdentity();
  const removeEntityMutation = useRemoveEntityIdentity();
  const addApiKeyMutation = useAddApiKeyIdentity();
  const removeApiKeyMutation = useRemoveApiKeyIdentity();

  return {
    // Entities
    entities: entitiesQuery.data || [],
    entitiesLoading: entitiesQuery.isLoading,
    entitiesError: entitiesQuery.error,
    refetchEntities: entitiesQuery.refetch,

    // API Keys
    apiKeys: apiKeysQuery.data || [],
    apiKeysLoading: apiKeysQuery.isLoading,
    apiKeysError: apiKeysQuery.error,
    refetchApiKeys: apiKeysQuery.refetch,

    // Entity mutations
    addEntity: addEntityMutation.mutate,
    addEntityAsync: addEntityMutation.mutateAsync,
    addEntityLoading: addEntityMutation.isPending,
    addEntityError: addEntityMutation.error,

    removeEntity: removeEntityMutation.mutate,
    removeEntityAsync: removeEntityMutation.mutateAsync,
    removeEntityLoading: removeEntityMutation.isPending,
    removeEntityError: removeEntityMutation.error,

    // API Key mutations
    addApiKey: addApiKeyMutation.mutate,
    addApiKeyAsync: addApiKeyMutation.mutateAsync,
    addApiKeyLoading: addApiKeyMutation.isPending,
    addApiKeyError: addApiKeyMutation.error,

    removeApiKey: removeApiKeyMutation.mutate,
    removeApiKeyAsync: removeApiKeyMutation.mutateAsync,
    removeApiKeyLoading: removeApiKeyMutation.isPending,
    removeApiKeyError: removeApiKeyMutation.error,
  };
}

/**
 * Hook to invalidate API identity queries
 */
export function useInvalidateApiIdentities() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.all,
      });
    },
    invalidateEntities: () => {
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.entities(),
      });
    },
    invalidateApiKeys: () => {
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.apiKeys(),
      });
    },
    invalidateEntity: (entityId: string) => {
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.entity(entityId),
      });
    },
    invalidateApiKey: (apiKeyId: string) => {
      queryClient.invalidateQueries({
        queryKey: apiIdentityQueryKeys.apiKey(apiKeyId),
      });
    },
  };
}

// ============================================================================
// LEGACY HOOK (for backward compatibility)
// ============================================================================

/**
 * Legacy hook that provides the API identity services
 * @deprecated Use individual hooks or useApiIdentities instead
 */
export const useApiIdentitiesService = () => {
  const service = useApiIdentityService();

  return {
    getEntities: service.getEntities.bind(service),
    addEntity: service.addEntity.bind(service),
    removeEntity: service.removeEntity.bind(service),

    getApiKeys: service.getApiKeys.bind(service),
    addApiKey: service.addApiKey.bind(service),
    removeApiKey: service.removeApiKey.bind(service),
  };
};
